import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IDocumentAnnotation } from '@apis/documentAnnotations';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import type { AnnotationsBlocker } from '../annotations.url';
import { DownloadIcon } from '@utils/icons';
import { AnnotationRow } from './AnnotationRow/AnnotationRow';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './AnnotationsSidebar.module.css';

interface IAnnotationsSidebarProps {
  draftCard?: ReactNode;
  annotations: readonly IDocumentAnnotation[];
  loading: boolean;
  loadFailed: boolean;
  onRetryLoad: () => void;
  tz: string | null;
  viewerUserId: string | null;
  isAdmin: boolean;
  isVisible: (id: string) => boolean;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onDownloadWithAnnotations: () => void;
  onDownloadOriginal: () => void;
  downloading: boolean;
  /** Set while the page cannot produce a copy yet — the document or its
   *  annotations are still loading, or the document failed. */
  blockedBy?: AnnotationsBlocker | null;
  /** Set when a download could not be produced — an encrypted PDF, or bytes
   *  that failed to fetch. */
  downloadError?: string | null;
  /** Set when a delete was refused — the row has come back. */
  deleteError?: string | null;
}

/**
 * The annotations on this version, newest first, each with the checkbox that
 * paints or hides it. The two downloads sit at the foot: the checked marks
 * flattened into a local copy, or the file exactly as the store holds it.
 */
export function AnnotationsSidebar({
  draftCard = null, annotations, loading, loadFailed, onRetryLoad, tz, viewerUserId, isAdmin,
  isVisible, onToggle, onDelete, onDownloadWithAnnotations, onDownloadOriginal, downloading,
  downloadError = null,
  deleteError = null,
  blockedBy = null,
}: IAnnotationsSidebarProps) {
  const { t } = useTranslation();
  const annotationsList = [...annotations].reverse();

  return (
    <aside className={styles.panel}>
      <div className={styles.head}>
        <h2 className={styles.title}>{t('documents.annotations.title')}</h2>
        <span className={styles.count}>
          {t('documents.annotations.onThisVersion', { count: annotations.length })}
        </span>
      </div>

      {draftCard}

      {loading ? (
        <p className={styles.empty}>{t('documents.annotations.loading')}</p>
      ) : loadFailed ? (
        <p className={styles.empty} role="alert">
          {t('documents.annotations.loadFailed')}{' '}
          <button type="button" className={styles.retry} onClick={onRetryLoad}>{t('documents.annotations.retry')}</button>
        </p>
      ) : annotationsList.length === 0 ? (
        <p className={styles.empty}>{t('documents.annotations.none')}</p>
      ) : (
        <ScrollArea className={styles.listWrap}>
          <ul className={styles.list}>
            {annotationsList.map((annotation) => (
              <AnnotationRow
                key={annotation.id}
                annotation={annotation}
                visible={isVisible(annotation.id)}
                tz={tz}
                canDelete={isAdmin || annotation.createdBy.userId === viewerUserId}
                onToggle={() => onToggle(annotation.id)}
                onDelete={() => onDelete(annotation.id)}
              />
            ))}
          </ul>
        </ScrollArea>
      )}

      {deleteError && <p className={styles.error} role="alert">{deleteError}</p>}

      <div className={styles.foot}>
        {/* One anchor for the pair: a disabled button dispatches no mouse
            events, so the reason has to hang off something around them. */}
        <Tooltip content={blockedBy ? t(`documents.annotations.blocked.${blockedBy}`) : ''}>
          <span className={styles.downloadAnchor}>
            <button
              type="button"
              className={styles.download}
              onClick={onDownloadWithAnnotations}
              disabled={downloading || blockedBy !== null}
            >
              <DownloadIcon size={14} />
              {t('documents.annotations.downloadWith')}
            </button>
            <button
              type="button"
              className={styles.downloadGhost}
              onClick={onDownloadOriginal}
              disabled={downloading || blockedBy !== null}
            >
              <DownloadIcon size={14} />
              {t('documents.annotations.downloadOriginal')}
            </button>
          </span>
        </Tooltip>
        {downloadError && <p className={styles.error} role="alert">{downloadError}</p>}
      </div>
    </aside>
  );
}
